(() => {
  const template = document.querySelector('[data-copyfast-review-template]');
  if (!template) return;
  const panel = template.content.firstElementChild.cloneNode(true);
  const warning = panel.querySelector('[data-artwork-warning]');
  const previewWarning = warning.cloneNode(true);
  previewWarning.className = 'copyfast-preview-warning';
  previewWarning.removeAttribute('data-artwork-warning');
  previewWarning.setAttribute('role', 'status');
  previewWarning.setAttribute('aria-live', 'polite');
  const unavailable = panel.querySelector('[data-artwork-unavailable]');
  const ready = panel.querySelector('[data-artwork-ready]');
  const checkbox = panel.querySelector('[data-artwork-confirm]');
  const sizeBox = panel.querySelector('[data-size-confirm]');   // second confirmation: size, colour and print area
  const bothChecked = () => checkbox.checked && (!sizeBox || sizeBox.checked);
  const error = panel.querySelector('[data-artwork-review-error]');
  const guideToggle = panel.querySelector('[data-artwork-guide-toggle]');
  const layerSelect = panel.querySelector('[data-artwork-layer]');
  const purchaseReview = document.createElement('div');
  purchaseReview.className = 'copyfast-purchase-review';
  purchaseReview.append(checkbox.closest('label'), error);
  const svgNS = 'http://www.w3.org/2000/svg';
  const guide = document.createElementNS(svgNS, 'svg');
  guide.classList.add('copyfast-print-guide');
  guide.setAttribute('aria-hidden', 'true');
  const shade = document.createElementNS(svgNS, 'path');
  shade.setAttribute('fill', 'rgba(17,32,51,.10)');
  shade.setAttribute('fill-rule', 'evenodd');
  const border = document.createElementNS(svgNS, 'polygon');
  border.setAttribute('fill', 'none');
  border.setAttribute('stroke', '#008b8b');
  border.setAttribute('stroke-width', '2');
  border.setAttribute('stroke-dasharray', '7 5');
  border.setAttribute('vector-effect', 'non-scaling-stroke');
  const safeBorder = border.cloneNode(true);
  safeBorder.setAttribute('data-artwork-safe-guide', '');
  border.setAttribute('stroke-opacity', '.9');
  safeBorder.style.display = 'none';
  safeBorder.setAttribute('stroke-dasharray', '4 4');
  guide.append(shade, border);   // one outline only: the real print area
  let canvas, timer, dragging = false, lastEdit = 0;
  const reset = () => { checkbox.checked = false; error.style.visibility = 'hidden'; };
  const uploads = () => [...document.querySelectorAll('input[type="file"][data-element-id]')];
  const uploadIds = () => new Set(uploads().map(input => Number(input.dataset.elementId)));
  const syncChoices = () => {
    const visible = new Set(layers(false).map(layer => Number(layer.id)));
    const choices = uploads().filter(input => visible.has(Number(input.dataset.elementId)));
    const signature = choices.map(input => `${input.dataset.elementId}:${input.name}`).join('|');
    if (!signature || layerSelect.dataset.choices === signature) return;
    const selected = layerSelect.value;
    layerSelect.replaceChildren(...choices.map(input => {
      const option = document.createElement('option');
      option.value = input.dataset.elementId;
      option.textContent = input.name.replace(/^properties\[/, '').replace(/\]$/, '');
      return option;
    }));
    if (choices.some(input => input.dataset.elementId === selected)) layerSelect.value = selected;
    layerSelect.dataset.choices = signature;
  };
  const layers = (filled = true) => (window.engraver?.imageHolderObjects || []).filter(layer =>
    uploadIds().has(Number(layer.id)) && layer.visible !== false &&
    (!layer.isVisiblePreview || layer.isVisiblePreview()) &&
    (!filled || (!layer._emptyImage && layer.originalElement?.width > 1)));
  const transform = (m, x, y) => ({ x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] });
  const corners = layer => [-1, 1].flatMap((y, row) => (row ? [1, -1] : [-1, 1]).map(x => transform(layer.calcTransformMatrix(), x * layer.width / 2, y * layer.height / 2)));
  const alphaCache = new WeakMap();
  let pendingUpload = null;
  const sourceKey = layer => layer.originalElement?.currentSrc || layer.originalElement?.src || layer.originalElement;
  const ink = layer => {
    const element = layer.originalElement;
    if (!element) return null;
    const key = sourceKey(layer), cached = alphaCache.get(element);
    if (cached?.key === key) return cached.value;
    let value = null;
    try {
      const width = element.naturalWidth || element.width, height = element.naturalHeight || element.height;
      if (!width || !height) return null;
      const scale = Math.min(1, 4096 / Math.max(width, height));
      const surface = document.createElement('canvas');
      surface.width = Math.ceil(width * scale); surface.height = Math.ceil(height * scale);
      const context = surface.getContext('2d', { willReadFrequently: true });
      context.drawImage(element, 0, 0, surface.width, surface.height);
      const pixels = context.getImageData(0, 0, surface.width, surface.height).data;
      const points = [];
      for (let y = 0; y < surface.height; y++) {
        let left = surface.width, right = -1;
        for (let x = 0; x < surface.width; x++) {
          if (pixels[(y * surface.width + x) * 4 + 3] > 1) { left = Math.min(left, x); right = x; }
        }
        if (right >= 0) {
          points.push([left / surface.width - .5, y / surface.height - .5],
            [(right + 1) / surface.width - .5, y / surface.height - .5],
            [left / surface.width - .5, (y + 1) / surface.height - .5],
            [(right + 1) / surface.width - .5, (y + 1) / surface.height - .5]);
        }
      }
      value = points.length ? points : null;
      surface.width = surface.height = 0;
    } catch (_) {}
    alphaCache.set(element, { key, value });
    return value;
  };
  const localInk = layer => {
    const box = layer._maskBoundsRectangle, points = ink(layer);
    if (!points || !box || layer.hasMask || layer.enablePerspective) return null;
    const frame = layer.calcTransformMatrix(), image = box.calcTransformMatrix();
    const det = frame[0] * frame[3] - frame[1] * frame[2];
    if (!det || ![...frame, ...image, layer.width, layer.height, box.width, box.height].every(Number.isFinite)) return null;
    return points.map(([x,y]) => {
      const point = transform(image, x * box.width, y * box.height);
      const dx = point.x - frame[4], dy = point.y - frame[5];
      return { x: (frame[3] * dx - frame[2] * dy) / det, y: (-frame[1] * dx + frame[0] * dy) / det };
    });
  };
  const extent = points => points.reduce((bounds, point) => ({
    left: Math.min(bounds.left, point.x), right: Math.max(bounds.right, point.x),
    top: Math.min(bounds.top, point.y), bottom: Math.max(bounds.bottom, point.y)
  }), { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity });
  const isClipped = layer => {
    const points = localInk(layer);
    if (!points) return null;
    const tolerance = Math.min(layer.width, layer.height) * .003;
    return points.some(point => Math.abs(point.x) > layer.width * .5 + tolerance || Math.abs(point.y) > layer.height * .5 + tolerance);
  };
  // Not cut off, but within 5% of the print area's edge: worth a note, never a block.
  const nearEdge = layer => { const points = localInk(layer); return !!points && points.some(point => Math.abs(point.x) > layer.width * .45 || Math.abs(point.y) > layer.height * .45); };
  const EDGE_NOTE = 'Your artwork is close to the edge of the print area. That is fine if you want it there.';
  const place = (layer, fit, resetRotation = false) => {
    layer.enableEditMode();
    if (resetRotation) layer.setEditModeImageRotation(0);
    let points = localInk(layer);
    if (!points) return false;
    if (fit || isClipped(layer)) {
      const bounds = extent(points);
      const ratio = Math.min(layer.width / (bounds.right - bounds.left), layer.height / (bounds.bottom - bounds.top)) * .96;
      layer.setEditModeImageScale(layer.getEditModeImageParameters().scaleX * ratio);
      points = localInk(layer);
    }
    const bounds = extent(points), frame = layer.calcTransformMatrix();
    const cx = (bounds.left + bounds.right) / 2, cy = (bounds.top + bounds.bottom) / 2;
    const params = layer.getEditModeImageParameters(), image = layer.item(0);
    layer.setEditModeImageOffset(params.offsetX - (frame[0] * cx + frame[2] * cy) / image.scaleX,
      params.offsetY - (frame[1] * cx + frame[3] * cy) / image.scaleY);
    canvas.fire('object:placeholder:maskModified', { object: layer, parameters: layer.getEditModeImageParameters() });
    canvas.requestRenderAll();
    window.engraver.processUpdateObjectModified();
    edited();
    return true;
  };
  // Move the artwork to a standard logo spot: (fx, fy) = its centre as a fraction of the print area, fw = its width as a fraction of the area's width.
  const spot = (layer, fx, fy, fw) => {
    layer.enableEditMode();
    layer.setEditModeImageRotation(0);
    let points = localInk(layer);
    if (!points) return false;
    let bounds = extent(points);
    const ratio = Math.min((layer.width * fw) / (bounds.right - bounds.left), (layer.height * fw) / (bounds.bottom - bounds.top));
    layer.setEditModeImageScale(layer.getEditModeImageParameters().scaleX * ratio);
    points = localInk(layer); bounds = extent(points);
    const frame = layer.calcTransformMatrix();
    const cx = (bounds.left + bounds.right) / 2 - (fx - .5) * layer.width, cy = (bounds.top + bounds.bottom) / 2 - (fy - .5) * layer.height;
    const params = layer.getEditModeImageParameters(), image = layer.item(0);
    layer.setEditModeImageOffset(params.offsetX - (frame[0] * cx + frame[2] * cy) / image.scaleX,
      params.offsetY - (frame[1] * cx + frame[3] * cy) / image.scaleY);
    canvas.fire('object:placeholder:maskModified', { object: layer, parameters: layer.getEditModeImageParameters() });
    canvas.requestRenderAll();
    window.engraver.processUpdateObjectModified();
    edited();
    return true;
  };
  const drawGuide = () => {
    const layer = layers(false).find(item => Number(item.id) === Number(layerSelect.value));
    const host = canvas?.wrapperEl;
    if (!host || !layer || !guideToggle.checked) { guide.style.display = 'none'; return; }
    if (guide.parentNode !== host) { host.style.position = 'relative'; host.append(guide); }
    guide.style.display = '';
    const width = canvas.width, height = canvas.height;
    guide.setAttribute('viewBox', `0 0 ${width} ${height}`);
    const points = corners(layer).map(point => transform(canvas.viewportTransform || [1,0,0,1,0,0], point.x, point.y));
    const center = points.reduce((sum, point) => ({x:sum.x+point.x/4,y:sum.y+point.y/4}), {x:0,y:0});
    safeBorder.setAttribute('points', points.map(point => `${center.x+(point.x-center.x)*.8},${center.y+(point.y-center.y)*.8}`).join(' '));
    const path = points.map(point => `${point.x},${point.y}`).join(' ');
    if (border.getAttribute('points') !== path) {
      border.setAttribute('points', path);
      shade.setAttribute('d', `M0 0H${width}V${height}H0Z M${points.map(point => `${point.x} ${point.y}`).join('L')}Z`);
    }
  };
  const show = (element, visible) => { element.style.visibility = visible ? 'visible' : 'hidden'; element.setAttribute('aria-hidden', String(!visible)); };
  const check = () => {
    if (dragging) return;
    try {
      syncChoices();
      const filled = layers();
      if (pendingUpload && Date.now() - pendingUpload.time < 30000) {
        const fresh = filled.find(layer => (pendingUpload.id === null || pendingUpload.id === Number(layer.id)) && pendingUpload.sources.get(Number(layer.id)) !== sourceKey(layer));
        if (fresh && ink(fresh)) { pendingUpload = null; layerSelect.value = String(fresh.id); const zipFront = /zip-hoodie/.test(location.pathname) && Number(fresh.id) === 1 && !/zip-hoodie-back/.test(location.pathname); const journal = /journal/.test(location.pathname); if (zipFront) spot(fresh, .75, .3, .38); else if (journal) spot(fresh, .8, .5, .34); else if (/bottle|tumbler/.test(location.pathname)) spot(fresh, .5, .5, /tumbler/.test(location.pathname) ? .3 : .45); else if (/mug/.test(location.pathname)) spot(fresh, .5, .5, .6); /* front cover, clear of the spine */ else spot(fresh, .5, .5, .8); if (isClipped(fresh)) place(fresh, true); }   // new artwork starts at 80% of the print area; the shopper can enlarge it
      }
      const results = filled.map(isClipped);
      const unknown = !window.engraver?.initReady || results.includes(null);
      const clipped = results.includes(true);
      const gallery = document.querySelector('.customily_gallery');
      if (gallery && previewWarning.parentNode !== gallery) gallery.prepend(previewWarning);
      const near = false;   // no inner safe-zone: only artwork that is actually cut off gets a warning
      if (!ready.dataset.text) ready.dataset.text = ready.textContent;
      let none = !filled.length; try { if (!none) none = !filled.some(layer => ink(layer)); } catch (_) {}
      { const readyText = none ? 'Upload your logo or artwork to see it on the garment.' : near ? EDGE_NOTE : ready.dataset.text; if (ready.textContent !== readyText) ready.textContent = readyText; }
      const message = clipped ? warning.textContent : unknown ? unavailable.textContent : none ? 'Upload your logo or artwork to see it here.' : near ? EDGE_NOTE : panel.querySelector('p').textContent;
      if (previewWarning.textContent !== message) previewWarning.textContent = message;
      previewWarning.dataset.clipped = String(clipped);
      show(previewWarning, true);
      show(warning, clipped); show(unavailable, unknown && !clipped); show(ready, !unknown && !clipped);
      panel.dataset.artworkCheck = clipped ? 'possible-clipping' : unknown ? 'unavailable' : 'review';
      panel.querySelectorAll('[data-artwork-action]').forEach(button => { button.disabled = !filled.some(layer => Number(layer.id) === Number(layerSelect.value)); });
      drawGuide();
    } catch (_) { previewWarning.textContent = unavailable.textContent; show(previewWarning, true); show(warning, false); show(ready, false); show(unavailable, true); panel.dataset.artworkCheck = 'unavailable'; reset(); }
  };
  const schedule = () => { clearTimeout(timer); timer = setTimeout(check, 400); };
  const edited = () => { lastEdit = Date.now(); reset(); schedule(); };
  const released = () => { dragging = false; schedule(); };
  const movingEvents = ['object:placeholder:maskModified', 'object:modified', 'object:moving', 'object:scaling', 'object:rotating'];
  const mount = () => {
    const gallery = document.querySelector('.customily_gallery');
    const buyRow = document.querySelector('#customily-cart-btn')?.closest('.product-form-buttons');
    if (buyRow && !panel.isConnected) buyRow.before(panel);
    if (buyRow && purchaseReview.nextElementSibling !== buyRow) buyRow.before(purchaseReview);
    if (window.engraver?.canvas && canvas !== window.engraver.canvas) {
      if (canvas) { movingEvents.forEach(name => canvas.off(name, edited)); canvas.off('mouse:up', released); canvas.off('after:render', drawGuide); }
      canvas = window.engraver.canvas;
      movingEvents.forEach(name => canvas.on(name, edited));
      canvas.on('mouse:up', released); canvas.on('after:render', drawGuide);
    }
    if (!dragging && Date.now() - lastEdit > 450) check();
  };
  panel.addEventListener('click', event => {
    const action = event.target.closest('[data-artwork-action]')?.dataset.artworkAction;
    if (!action) return;
    const layer = layers().find(item => Number(item.id) === Number(layerSelect.value));
    if (!layer || !canvas) return;
    if (action === 'left-chest' || action === 'right-chest') { const zip = /zip-hoodie/.test(location.pathname); spot(layer, action === 'left-chest' ? (zip ? .75 : .71) : (zip ? .25 : .29), zip ? .3 : .21, zip ? .38 : .36); return; }
    place(layer, action === 'fit' || action === 'reset', action === 'reset');
  });
  document.addEventListener('pointerdown', event => { if (event.target.closest('.canvas-container')) { dragging = true; clearTimeout(timer); } }, true);
  document.addEventListener('pointerup', released, true);
  document.addEventListener('pointercancel', released, true);
  window.addEventListener('blur', released);
  const blockUnreviewed = event => {
    dragging = false; clearTimeout(timer); check();
    // artwork past the print area is allowed when the shopper has ticked the review box: some designs are meant to run off the edge
    if (bothChecked()) return;
    event.preventDefault(); event.stopImmediatePropagation();
    error.style.visibility = 'visible';
    purchaseReview.scrollIntoView({ behavior: 'smooth', block: 'center' });
    checkbox.focus({ preventScroll: true });
  };
  document.addEventListener('click', event => {
    if (event.target.closest('#customily-cart-btn, button[name="add"], .shopify-payment-button')) blockUnreviewed(event);
    if (event.target.closest('.cl_remove-image')) edited();
  }, true);
  document.addEventListener('submit', event => { if (event.target.matches('form[action*="/cart/add"]')) blockUnreviewed(event); }, true);
  document.addEventListener('change', event => {
    if (event.target === checkbox || event.target === sizeBox) { if (bothChecked()) error.style.visibility = 'hidden'; return; }
    if (event.target === guideToggle || event.target === layerSelect) { drawGuide(); check(); return; }
    if (event.target.matches('input[type="file"]')) {
      if (event.isTrusted !== false) pendingUpload = { time: Date.now(), id: event.target.dataset.elementId ? Number(event.target.dataset.elementId) : null, sources: new Map(layers().map(layer => [Number(layer.id), sourceKey(layer)])) };
      edited();
    } else if (event.target.matches('input[data-option-name]')) edited();
    else schedule();
  }, true);
  window.addEventListener('customily-update-customization', schedule);
  window.addEventListener('resize', drawGuide);
  mount();
  const lifecycle = setInterval(() => { if (template.isConnected) mount(); else { clearInterval(lifecycle); clearTimeout(timer); guide.remove(); } }, 1000);
})();
